import React, { useEffect, useState } from 'react';
import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import {
  Backdrop,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Container,
  CssBaseline,
  Grid,
  IconButton,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Fab, Zoom, useScrollTrigger,
  useMediaQuery,
  useTheme
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Email,
  Password,
  ContactMail,
  AddressBook, 
  PersonAdd,
  Edit,
  ChevronRight,
  ExpandMore,
} from '@mui/icons-material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  createTheme,
  ThemeProvider,
} from '@mui/material/styles';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "../accordion";
import {
  FormInputText,
} from "../../components/form";
import {
  AlertDialog,
  ConfirmDialog,
} from "../../components/dialog";
import TextMaskCustom from "../../components/form/inputProps/TextMaskCustom"
import { uploadFilePath, fileServerUrl } from '../../config';
import { objectEmptyCheck } from "../../utils";
import * as sessionActions from "../../store/session";
import * as menuActions from '../../store/menu';
import * as g04docuFileActions from "../../store/g04docuFile";
import * as gclientTypeActions from "../../store/gclientType";
import * as gclientType04docuActions from "../../store/gclientType04docu";
import * as gclientActions from "../../store/gclient";
import * as userActions from "../../store/user";

import CertifiedBox from "./CertifiedBox";
import PasswordChangeDialog from "../Password/PasswordChangeDialog";
import EmailSettingDialog from "./EmailSettingDialog";
import Users from "./Users";
import Security from "./Security";
import G04GeneralDocu from "./G04GeneralDocu";
import G04docuGCertification from "./G04docuGCertification";
import G04docuGTestRawMaterial from "./G04docuGTestRawMaterial";
import G04docuGTestSubMaterial from "./G04docuGTestSubMaterial";
import G04docuGTestProcess from "./G04docuGTestProcess";
import './Profile.css';
import G04docuEtc from './G04docuEtc';
import ScrollToTop from '../../utils/scrollUtils';
import AddressBookDialog from "./AddressBookDialog";
import DocumentViewerDrawer from "../common/viewer/DocumentViewerDrawer";

const defaultValues = {
  id: "",
  name: "",
  bizRegNumber: "",
  password: "",
  confirmPassword: "",
  phone: "",
  fax: "",
  address: "",
  ceo: "",
  email: "",

  // 날인문구
  // supplierLabel: "제공사",
  supplierLabel: "",
  supplier: "",
  phrase: "",

  userId: "",
  userName: "",
  mobile: "",
};

const validateFields = {
  name: "회사명",
  ceo: "대표자",
  phone: "대표전화번호",
  address: "주소",
  email: "회사대표메일",
};

const theme = createTheme();

const mobileSectionTitleSx = {
  fontWeight: 700,
  mb: 1.5,
  pb: 1,
  borderBottom: '1px solid #e0e0e0',
};

const mobileTypeBarSx = {
  mx: -2,
  mt: -2,
  px: 2,
  py: 1.25,
  bgcolor: '#f3f4f6',
  borderTop: '1px solid',
  borderBottom: '1px solid',
  borderColor: 'divider',
};

const mobileActionRowSx = {
  justifyContent: 'flex-start',
  height: 48,
  px: 0.5,
  borderRadius: 0,
  color: 'text.primary',
  textTransform: 'none',
  fontSize: '1rem',
  borderBottom: '1px solid',
  borderColor: 'divider',
  '& .MuiButton-endIcon': { marginLeft: 'auto' },
};

function ReadOnlyField({ label, value }) {
  return (
    <Box sx={{ py: 0.75 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {value ? String(value) : '-'}
      </Typography>
    </Box>
  );
}

function MobileClientTypes({
  isAdmin,
  gclientTypes,
  selectedGClientTypeIds,
  groups,
  activeTypeId,
  onToggleType,
  onToggleDetail,
  onExpandType,
}) {
  const [expandedId, setExpandedId] = useState(activeTypeId ?? null);

  useEffect(() => {
    if (activeTypeId) {
      setExpandedId(activeTypeId);
    }
  }, [activeTypeId]);

  const groupOf = (typeId) => groups.find((group) => group.gclientTypeId === typeId);
  const selectedDetails = (group) => (
    group ? group.gclientType04docus.filter((detail) => group.selectedGClientType04docuIds.includes(detail.id)) : []
  );

  const toggleExpand = (typeId) => {
    setExpandedId((current) => (current === typeId ? null : typeId));
    onExpandType(typeId);
  };

  const toggleDetail = (group, detailId) => {
    const selected = group.selectedGClientType04docuIds.includes(detailId);
    const next = selected
      ? group.selectedGClientType04docuIds.filter((id) => id !== detailId)
      : [...group.selectedGClientType04docuIds, detailId];
    onToggleDetail(null, next, group.gclientTypeId, 0);
  };

  if (!isAdmin) {
    const selectedTypes = gclientTypes.filter((type) => selectedGClientTypeIds.includes(type.id));
    return (
      <Box>
        <Typography variant="subtitle2" sx={mobileTypeBarSx}>{"대분류"}</Typography>
        {selectedTypes.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ pt: 1.5 }}>{"선택된 대분류가 없습니다."}</Typography>
        ) : (
          <Box>
            {selectedTypes.map((type) => {
              const names = selectedDetails(groupOf(type.id)).map((detail) => detail.name).join(' · ');
              return (
                <Box key={type.id} sx={{ py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography sx={{ fontWeight: 700 }}>{type.name}</Typography>
                  {names && (
                    <Typography variant="body2" color="text.secondary">{names}</Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={mobileTypeBarSx}>{"대분류"}</Typography>
      <Box>
        {gclientTypes.map((type) => {
          const group = groupOf(type.id);
          const details = group?.gclientType04docus || [];
          const chosen = selectedDetails(group);
          const selected = selectedGClientTypeIds.includes(type.id);
          const expanded = expandedId === type.id;
          return (
            <Box key={type.id} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', minHeight: 48 }}
                onClick={() => { if (details.length > 0) toggleExpand(type.id); }}
              >
                <Checkbox
                  checked={selected}
                  onClick={(event) => event.stopPropagation()}
                  onChange={() => onToggleType(type.id)}
                  inputProps={{ 'aria-label': type.name }}
                />
                <Typography sx={{ flex: 1, fontWeight: 700 }}>{type.name}</Typography>
                {details.length > 0 && selected && !expanded && chosen.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                    {`세분류 ${chosen.length}`}
                  </Typography>
                )}
                {details.length > 0 && (
                  <IconButton
                    aria-label={expanded ? "세분류 접기" : "세분류 펼치기"}
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleExpand(type.id);
                    }}
                  >
                    <ExpandMore sx={{ transform: expanded ? 'rotate(180deg)' : 'none' }} />
                  </IconButton>
                )}
              </Box>
              {details.length > 0 && expanded && (
                <Box sx={{ pl: 1, pb: 0.5 }}>
                  {details.map((detail) => {
                    const detailSelected = group.selectedGClientType04docuIds.includes(detail.id);
                    return (
                      <Box
                        key={detail.id}
                        sx={{ display: 'flex', alignItems: 'center', minHeight: 44, pl: 4 }}
                        onClick={() => { if (selected) toggleDetail(group, detail.id); }}
                      >
                        <Checkbox
                          size="small"
                          checked={detailSelected}
                          disabled={!selected}
                          onClick={(event) => event.stopPropagation()}
                          onChange={() => { if (selected) toggleDetail(group, detail.id); }}
                          inputProps={{ 'aria-label': detail.name }}
                        />
                        <Typography variant="body2">{detail.name}</Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

/** 데스크톱은 아코디언, 모바일은 내용만. hidden이어도 폼 값은 유지된다. */
function ProfileSection({ accordion, expanded, onChange, summary, hidden, children }) {
  const body = accordion ? (
    <Accordion expanded={expanded} onChange={onChange}>
      {summary}
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  ) : children;

  return (
    <Grid item xs={12} sx={{ display: hidden ? 'none' : 'block' }}>
      {body}
    </Grid>
  );
}

const requiredMessage = "을(를) 입력해주세요.";
const emailMessage = "을(를) 올바른 이메일 형태로 입력해주세요.";

const validateSchema = {
  // bizRegNumber: yup.string().required(requiredMessage),
  name: yup.string().required(requiredMessage),
  ceo: yup.string().required(requiredMessage),
  phone: yup.string().required(requiredMessage),
  // password: yup.string().required(requiredMessage),
  // confirmPassword: yup.string().required(requiredMessage),
  address: yup.string().required(requiredMessage),
  email: yup.string().email(emailMessage).required(requiredMessage),
}

const schema = yup.object(validateSchema).required();

// 회원사 수정은 빠뜨린 칸을 비운다. 문구·비밀번호·인감은 값이 없을 때 기존 값을 유지한다.
const keptGClientColumns = (user = {}) => ({
  id: user.id,
  gclientTypes: user.gclientTypes,
  gclientType04docus: user.gclientType04docus,
  name: user.name,
  phone: user.phone,
  address: user.address,
  email: user.email,
  inCharges: user.inCharges,
  systemUrl: user.systemUrl,
  ceo: user.ceo,
  fax: user.fax,
  joinDate: user.joinDate,
  products: user.products,
  zipCode: user.zipCode,
  phone2: user.phone2,
  otherPhones: user.otherPhones,
  address2: user.address2,
  otherAddresses: user.otherAddresses,
});

const profileValuesFromSession = (user) => {
  const values = { ...defaultValues };
  if (!user) return values;
  for (const key of Object.keys(defaultValues)) {
    if (user[key] !== undefined && user[key] !== null && user[key] !== '') {
      values[key] = user[key];
    }
  }
  if (user.phrases) {
    values.supplierLabel = user.phrases.supplierLabel || "";
    values.supplier = user.phrases.supplier || "";
    values.phrase = user.phrases.phrase || "";
  }
  if (user.sessionUserLoginType === 'USER' && user.user) {
    values.userId = user.user.userId || "";
    values.userName = user.user.name || "";
    values.mobile = user.user.mobile || "";
  }
  return values;
};

const Profile = () => {
  const sessionUser = useSelector((state) => state.session.sessionUser);
  const gclientTypes = useSelector((state) => state.gclientType.gclientTypes);
  const sessionUserRole = useSelector((state) => state.session.sessionUser.role);
  const gclientType04docus = useSelector((state) => state.gclientType04docu.gclientType04docus);
  const sessionUserLoginType = useSelector((state) => state.session.sessionUser.sessionUserLoginType);
  const { handleSubmit, setValue, control, getValues, watch, formState: { errors } } = useForm({
    defaultValues: profileValuesFromSession(sessionUser),
    resolver: yupResolver(schema),
  });
  
  const muiTheme = useTheme();
  // noSsr: true로 설정하여 초기 렌더링 시에도 정확한 값 반환
  // defaultMatches: true로 설정하여 초기값을 모바일로 가정 (불필요한 API 호출 방지)
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'), { noSsr: true, defaultMatches: true });
  const isAdmin = sessionUserRole === 'ADMIN_ROLE';
  const [mobileTab, setMobileTab] = useState(0);
  const [expandedMobileTypeId, setExpandedMobileTypeId] = useState(null);
  const showMobileSaveBar = isMobile && isAdmin && (mobileTab === 0 || mobileTab === 2);
  const profileValues = watch();

  // no dispatch
  const downloadG04docuFile = (documentPath) => g04docuFileActions.downloadDirect(documentPath)
  const selectAllGClientDirect = () => gclientActions.selectAllDirect()

  // dispatch
  const dispatch = useDispatch();

  const modifyUser = ({ id, userId, name, mobile, gclientId }) => dispatch(userActions.modify({ id, userId, name, mobile, gclientId }))
  const deleteUser = (id) => dispatch(userActions.remove(id))
  const gclientLeave = (id) => dispatch(gclientActions.leave(id));
  const modifyGClient = ({ id, gclientTypes, gclientType04docus, name, phone, address, email, inCharges, systemUrl, phrases, ceo, fax, joinDate, products, zipCode, phone2, otherPhones, address2, otherAddresses }) => dispatch(gclientActions.modify({ id, gclientTypes, gclientType04docus, name, phone, address, email, inCharges, systemUrl, phrases, ceo, fax, joinDate, products, zipCode, phone2, otherPhones, address2, otherAddresses }))
  const modifyPhrases = ({ id, phrases }) => dispatch(gclientActions.modifyPhrases({ id, phrases }))
  const selectAllByGClient = (gclientId) => dispatch(userActions.selectAllByGClient(gclientId))
  const selectGClientTypes = () => dispatch(gclientTypeActions.selectAll())
  const selectGClientType04docus = () => dispatch(gclientType04docuActions.selectAll())

  const [phoneMask, setPhoneMask] = useState("000-0000-0000");
  const [mobileMask, setMobileMask] = useState("000-0000-0000");

  // 헤더 토글
  const [basicExpanded, setBasicExpanded] = useState(true);
  const [signExpanded, setSignExpanded] = useState(true);
  // 

  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [selectedGClientId, setSelectedGClientId] = useState("");
  const [selectedGClient, setSelectedGClient] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedGClientTypeIds, setSelectedGClientTypeIds] = useState([]); // 대분류 선택 id 배열
  const [gclientTypeAndGClientType04docu, setGClientTypeAndGClientType04docu] = useState([]); // 대분류와 하위의 세분류와 세분류 선택 id 배열 및 보이기 여부 플래그 포함 데이터 구조

  // 비밀번호 변경 다이얼로그 관련
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [mode, setMode] = useState("");
  const [type, setType] = useState("");
  const [openEmailAppPasswordDialog, setOpenEmailAppPasswordDialog] = useState(false);
  const [openEmailAppPasswordDialogUser, setOpenEmailAppPasswordDialogUser] = useState(false);
  const [openAddressBookDialog, setOpenAddressBookDialog] = useState(false);

  // 

  const [alertInfo, setAlertInfo] = useState({});

  // 회원탈퇴
  const [leaveObject, setLeaveObject] = useState({});
  const [confirmOpenLeave, setConfirmOpenLeave] = useState(false);

  // 미리보기
  const [documentViewerDrawerOpen, setDocumentViewerDrawerOpen] = useState(false);
  const [currentUrl, setCurrentUrl] = useState("");

  useEffect(
    () => {
      if (!objectEmptyCheck(errors)) {
        console.log(errors)
        let error;
        for (const [key, value] of Object.entries(validateFields)) {
          // console.log(`${key}: ${value}`);
          if (errors[key]) {
            error = errors[key];
            error.fieldName = value;
            break;
          }
        }

        // console.log(error)
        if (error) {
          setAlertInfo({
            titleAlert: "안내",
            messageAlert: `${error?.fieldName}${error?.message}`,
            open: true,
          });
        }
      }
    }, [errors]
  )

  const [URLThumbnail, setURLThumbnail] = useState();

  // 스크롤 트리거 및 스크롤 동작 추가
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const createImageURL = (fileBlob) => {  // createObjectURL 방식
    if (URLThumbnail) URL.revokeObjectURL(URLThumbnail);

    const url = URL.createObjectURL(fileBlob);

    setURLThumbnail(url);
  };

  const onImageChange = (e) => {
    const { files } = e.target;

    if (!files || !files[0]) return;

    const uploadImage = files[0];

    createImageURL(uploadImage);
  };

  useEffect(
    async () => {
      setOpenBackdrop(true);

      const { sessionUserLoginType } = sessionUser;
      if (sessionUserLoginType === "GCLIENT") {
        if (sessionUser.initPassword) {
          setOpenPasswordDialog(true);
          setMode('C');
        }
      } else if (sessionUserLoginType === "USER") {
        if (sessionUser.user.initPassword) {
          setOpenPasswordDialog(true);
          setMode('C');
        }
      }

      // 기본정보 설정
      for (const [item, value] of Object.entries(defaultValues)) {
        setValue(item, sessionUser[item] || value);
      }

      if (sessionUser["phrases"]) {
        const { supplierLabel, supplier, phrase } = sessionUser["phrases"];
        setValue("supplierLabel", supplierLabel);
        setValue("supplier", supplier);
        setValue("phrase", phrase);
      }

      const { id: gclientId } = sessionUser;

      // TODO : redux 데이터가 보낸 순서대로 들어오는지 파악 필요. 지금은 그렇다고 가정하고 되어 있음. 문제발생시 이 문제때문인지 아닌지 우선 고려할 것
      await selectGClientTypes();
      await selectGClientType04docus();
      
      // 모바일에서는 서류 섹션이 숨겨져 있으므로 gclient 상세 정보를 불러오지 않음
      if (!isMobile) {
        //const resGClients = await selectAllGClientDirect();
        //const gclient = resGClients.find(gclient => gclient.id === gclientId);
        const gclient = await gclientActions.selectDirect(gclientId);

        console.log({ gclient })

        setSelectedGClientId(gclientId);
        setSelectedGClient(gclient);
      } else {
        setSelectedGClientId(gclientId);
      }

      // 회원사 사용자일 때 정보 설정
      if (sessionUserLoginType === 'USER') {
        const { user } = sessionUser;
        if (user) {
          const { userId, name, mobile } = user;
          setValue("userId", userId);
          setValue("userName", name);
          setValue("mobile", mobile);
        }
      }
      
      setOpenBackdrop(false);
    }, [dispatch, isMobile, sessionUser?.id]
  )

  // 초기 페이지 로딩할 때 거래선 구분값 목록 구성 및 설정
  useEffect(
    () => {
      if (gclientTypes.length > 0 && gclientType04docus.length > 0) {
        const { gclientTypes: myGClientTypes = [], gclientType04docus: myGClientType04docus = [] } = sessionUser;
        
        console.log({ myGClientTypes, myGClientType04docus })
        if (myGClientTypes && Array.isArray(myGClientTypes)) {
          // 대분류 설정 : 대분류 목록은 이미 gclientTypes를 기반으로 만들어져 있고 접속회사의 대분류 구분 값을 설정한다.
          const newSelectedGClientTypeIds = myGClientTypes.map(type => type.id);
          setSelectedGClientTypeIds(newSelectedGClientTypeIds);

          // 세분류 설정
          if (myGClientType04docus && Array.isArray(myGClientType04docus)) {
            // 세분류 목록 및 값 설정
            const newGClientTypeAndGClientType04docu = gclientTypes.map(gclientType => {
              const { id, name } = gclientType;
              return {
                gclientTypeId: id, // 대분류 아이디
                gclientTypeName: name, // 대분류 이름
                gclientType04docus: gclientType04docus.filter(gclientType04docu => gclientType04docu.gclientTypeId === id), // 세분류 목록
                selectedGClientType04docuIds: myGClientType04docus.filter(gclientType04docu => gclientType04docu.gclientTypeId === id).map(type => type.id), // 세분류 설정값
                show: newSelectedGClientTypeIds.includes(id), // 세분류 보이기 옵션
              }
            });
            console.log({ newGClientTypeAndGClientType04docu })
            setGClientTypeAndGClientType04docu(newGClientTypeAndGClientType04docu);
          }
        }
      }
    }, [gclientTypes, gclientType04docus, sessionUser]
  )

  const onSubmit = async ({ id, name, phone, address, email, userName, mobile, ceo, fax }) => {
    try {
      setLoading(true);

      if (sessionUserRole === 'ADMIN_ROLE') {
        const myGClientTypes = gclientTypes.filter(gclientType => selectedGClientTypeIds.includes(gclientType.id));
        // 아래 코드에서 filter를 통해 대분류가 설정되지 않은 그 하위의 세분류 항목은 제외한다. handleChangeGClientTypes 함수의 설명과 같이 이해
        const selectedGClientType04docuIds = [].concat(
          ...gclientTypeAndGClientType04docu.filter(gclientType => {
            const { gclientTypeId, show, selectedGClientType04docuIds } = gclientType;
            
            if (selectedGClientTypeIds.includes(gclientTypeId) && show && selectedGClientType04docuIds.length > 0) {
              return true;
            } else {
              return false;
            }
          }).map(item => item.selectedGClientType04docuIds)
        ); // arr1d = [].concat(...arr2d)
        // console.log({ selectedGClientTypeIds, selectedGClientType04docuIds, gclientTypeAndGClientType04docu });
        const myGClientType04docus = gclientType04docus
                                      .filter(gclientType04docu => selectedGClientType04docuIds.includes(gclientType04docu.id))
                                      .map(item => ({ ...item, gclientTypeName: myGClientTypes.find(type => type.id === item.gclientTypeId)?.name }));

        // 문구는 보내지 않는다. 날인에서 고친 값이 같이 저장되지 않고, 저장된 문구는 유지된다.
        await modifyGClient({
          ...keptGClientColumns(sessionUser),
          id,
          gclientTypes: myGClientTypes,
          gclientType04docus: myGClientType04docus,
          name,
          phone,
          address,
          email,
          ceo,
          fax,
        });
          
        if (sessionUserLoginType === 'USER') {
          await modifyUser({
            id: sessionUser.user.id,
            userId: sessionUser.user.userId,
            name: userName,
            mobile,
            gclientId: selectedGClientId,
          });
  
          selectAllByGClient(selectedGClientId);
        }
  
        dispatch(sessionActions.restoreSessionUser());
      } else if (sessionUserRole === 'USER_ROLE') {
        await modifyUser({
          id: sessionUser.user.id,
          userId: sessionUser.user.userId,
          name: userName,
          mobile,
          gclientId: selectedGClientId,
        });
  
        dispatch(sessionActions.restoreSessionUser());
      }
    } catch (e) {
      setTimeout(() => setLoading(false), 1000);
    } finally {
      setTimeout(() => setLoading(false), 1000);
    }
  };

  const onSubmitStamp = async () => {
    if (sessionUserRole !== 'ADMIN_ROLE') return;

    try {
      setLoading(true);
      const { supplierLabel, supplier, phrase } = getValues();
      await modifyPhrases({
        id: sessionUser.id,
        phrases: {
          supplierLabel: supplierLabel || "",
          supplier: supplier || "",
          phrase: phrase || "",
        },
      });
      dispatch(sessionActions.restoreSessionUser());
    } catch (e) {
      setTimeout(() => setLoading(false), 1000);
    } finally {
      setTimeout(() => setLoading(false), 1000);
    }
  };

  const handleClickFileDownload = async (documentPath) => {
    const arr = documentPath.split("/");
    const fileName = arr[arr.length - 1];
  
    const res = await downloadG04docuFile(documentPath);
    const blob = await res.blob(); // res.blob는 promise를 리턴함???
    
    // 2. Create blob link to download
    const url = window.URL.createObjectURL(new Blob([blob]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    // 3. Append to html page
    document.body.appendChild(link);
    // 4. Force download
    link.click();
    // 5. Clean up and remove the link
    link.parentNode.removeChild(link);
  }

  const handleChangeGClientTypes = (e, newSelectedGClientIds) => {
    const added = newSelectedGClientIds.find((id) => !selectedGClientTypeIds.includes(id));
    if (added) {
      setExpandedMobileTypeId(added);
    } else if (!newSelectedGClientIds.includes(expandedMobileTypeId)) {
      setExpandedMobileTypeId(newSelectedGClientIds[0] || null);
    }
    setSelectedGClientTypeIds(newSelectedGClientIds);

    const newGClientTypeAndGClientType04docu = gclientTypeAndGClientType04docu.map(gclientType => {
      const { gclientTypeId } = gclientType;
      return {
        ...gclientType,
        show: newSelectedGClientIds.includes(gclientTypeId),
      }
    });

    setGClientTypeAndGClientType04docu(newGClientTypeAndGClientType04docu);
  }

  const toggleMobileGClientType = (id) => {
    const next = selectedGClientTypeIds.includes(id)
      ? selectedGClientTypeIds.filter((typeId) => typeId !== id)
      : [...selectedGClientTypeIds, id];
    handleChangeGClientTypes(null, next);
  };

  const handleChangeGClientTypeAndGClientType04docu = (e, newSelected, gclientTypeId, idx) => {
    console.log({ e, newSelected, gclientTypeId, idx })
    // newSelected에 선택한 값의 배열이 그대로 들어오므로 기존 선택된 배열과 merge 등의 가공을 할 필요가 없음. (참고) const merge = [...new Set([...arr1, ...arr2])];
    const newGClientTypeAndGClientType04docu = gclientTypeAndGClientType04docu.map(gclientType => {
      if (gclientType.gclientTypeId === gclientTypeId) {
        return {
          ...gclientType,
          selectedGClientType04docuIds: newSelected,
        }
      } else {
        return gclientType;
      }
    });
    
    setGClientTypeAndGClientType04docu(newGClientTypeAndGClientType04docu);
  }

  // const handleClickViewEachDoc = (documentPath) => {
  //   // setOpenProgress(true);
  //   // setShowFileViewer(false);
    
  //   // const ext = getFileType(documentPath)
  //   // setFileType(ext);

  //   const path = documentPath.replace(uploadFilePath, fileServerUrl);
  //   // console.log(path)
  //   // setDocumentPath(path);
    
  //   // setTimeout(() => {
  //   //   setOpenProgress(false);
  //   //   setShowFileViewer(true);
  //   // }, 1000);

  //   // 캐싱으로 인해 이전 문서가 계속 보이는 문제 해결
  //   const randNumber = Math.floor(Math.random()*99);

  //   window.open(`${path}?q=cat&${randNumber}`, "미리보기", 'target="_self"');
  // }

  const handleClickViewEachDoc = (documentPath) => {
    const pathParts = documentPath.replace(uploadFilePath, '').split('/');
    
    // 캐싱으로 인해 이전 문서가 계속 보이는 문제 해결
    const randNumber = Math.floor(Math.random()*99);
    
    // 경로를 '/'로 분리하고 각 부분을 개별적으로 인코딩
    const encodedPath = pathParts.map(part => encodeURIComponent(part)).join('/');

    const path = `${fileServerUrl}${encodedPath}?q=cat&${randNumber}`;

    // window.open(`${path}?q=cat&${randNumber}`, "미리보기", 'target="_self"');
    setCurrentUrl(path);
    if (!documentViewerDrawerOpen) {
      setDocumentViewerDrawerOpen(true);
    }
  }

  const handleClickPasswordChange = (e) => {
    e.stopPropagation();

    setOpenPasswordDialog(true);
    setMode('C');
  }

  const handleChangePhone = (e, type) => {
    const { value } = e.target;
    const fieldName = type === 'fax' ? 'fax' : 'phone';

    const areaCode02 = "02";
    const compareCode = value.substring(0, 2);
    if (compareCode === areaCode02) {
      if (value.length < 12) {
        setPhoneMask("00-000-00000"); // 한자리 여분을 두어야 함. 00-000-0000하면 더 이상 입력이 안됨
      } else {
        setPhoneMask("00-0000-0000");
      }
    } else {
      if(value.length < 13) {
        setPhoneMask("000-000-00000"); // 한자리 여분을 두어야 함
      } else {
        setPhoneMask("000-0000-0000");
      }
    }
    
    //setValue('phone', value);
    setValue(fieldName, value);
  }

  const handleChangeMobile = (e) => {
    const { value } = e.target;
    
    if(value.length < 13) {
      setMobileMask("000-000-00000"); // 한자리 여분을 두어야 함
    } else {
      setMobileMask("000-0000-0000");
    }
    
    setValue('mobile', value);
  }

  const handleClickLeave = () => {
    // selectedGClientId
    let id = "";
    if (sessionUserLoginType === 'GCLIENT') {
      id = selectedGClientId;
    } else if (sessionUserLoginType === 'USER') {
      const { user } = sessionUser;
      if (user) {
        id = user.id;
      }
    }
    
    setLeaveObject({ id });
    setConfirmOpenLeave(true);
  }

  const leave = async ({ id }) => {
    try {
      let messageAlert;
      if (sessionUserLoginType === 'GCLIENT') {
        messageAlert = (<>
          <span>{"탈퇴가 완료되었습니다."}</span><br />
          <span>{"3개월이 지나야 재가입하실 수 있습니다."}</span><br />
          <span>{"그동안 공사다큐 서비스를 사용해 주셔서 감사합니다."}</span>
        </>);
        
        await gclientLeave(id); // 실제로 삭제하지 않고 GClients의 valid = false, leaveDate 기록
      } else if (sessionUserLoginType === 'USER') {
        messageAlert = (<>
          <span>{"탈퇴가 완료되었습니다."}</span><br />
          <span>{"그동안 공사다큐 서비스를 사용해 주셔서 감사합니다."}</span>
        </>);
        
        await deleteUser(id); // 사용자는 실제 삭제
      }
  
      // 먼저 로그아웃 하고 나서 삭제 => 탈퇴 처리후 로그아웃으로 변경
      await dispatch(sessionActions.logout(sessionUser));
      await dispatch(menuActions.removeAllTabMenus());
  
      setAlertInfo({
        titleAlert: "안내",
        messageAlert,
        open: true,
      });
    } catch(e) {
      // TODO : 예외처리 필요
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={openBackdrop}
        // onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Container component="main" maxWidth="lg" sx={{ pb: showMobileSaveBar ? 10 : 0 }}>
        <CssBaseline />
        <Box
          sx={{
            // marginTop: 8,
            // display: 'flex', // 이 속성이 있을 경우 <Box>안에 있는 컴포넌트의 가로 사이즈가 늘어나면 <BOX>도 커짐
            // flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              {isMobile && isAdmin && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      position: 'sticky',
                      top: '68.5px',
                      zIndex: (t) => t.zIndex.appBar,
                      bgcolor: '#fff',
                      mx: -2,
                      borderBottom: '1px solid #dcdcdc',
                    }}
                  >
                    <Tabs
                      value={mobileTab}
                      onChange={(event, value) => setMobileTab(value)}
                      variant="fullWidth"
                      sx={{
                        '& .MuiTab-root': {
                          minWidth: 0,
                          px: 0.5,
                        },
                      }}
                    >
                      <Tab label="기본정보" />
                      <Tab label="담당자" />
                      <Tab label="날인" />
                      <Tab label="보안" />
                    </Tabs>
                  </Box>
                </Grid>
              )}
              {isMobile && !isAdmin && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      position: 'sticky',
                      top: '68.5px',
                      zIndex: (t) => t.zIndex.appBar,
                      bgcolor: '#fff',
                      mx: -2,
                      borderBottom: '1px solid #dcdcdc',
                    }}
                  >
                    <Tabs
                      value={mobileTab}
                      onChange={(event, value) => setMobileTab(value)}
                      variant="fullWidth"
                      sx={{
                        '& .MuiTab-root': {
                          minWidth: 0,
                          px: 0.5,
                        },
                      }}
                    >
                      <Tab label="내정보" />
                      <Tab label="회사정보" />
                      <Tab label="날인" />
                    </Tabs>
                  </Box>
                </Grid>
              )}
              {/* 기본정보. 모바일 관리자는 첫 탭, 모바일 사용자는 내정보·회사정보 탭. */}
              <ProfileSection
                accordion={!isMobile}
                expanded={basicExpanded}
                onChange={() => setBasicExpanded(!basicExpanded)}
                hidden={(isMobile && isAdmin && mobileTab !== 0) || (isMobile && !isAdmin && mobileTab === 2)}
                summary={(
                  <AccordionSummary aria-controls="basic-content" id="basic-header">
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={10}>
                        <Typography variant="h6" component="div">{sessionUserLoginType === "GCLIENT" ? "기본정보" : "회원사 정보"}</Typography>
                      </Grid>
                      {
                        sessionUserLoginType === 'GCLIENT' && (
                          <Grid item xs={12} sm={2} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end', alignItems: 'center' }}>
                            <Button
                              onClick={handleClickPasswordChange}
                              fullWidth
                              variant="outlined"
                              startIcon={<Password />}
                            >
                              {"비밀번호 변경"}
                            </Button>
                          </Grid>
                        )
                      }
                    </Grid>
                  </AccordionSummary>
                )}
              >
                    <Grid container spacing={2}>
                      {isMobile && isAdmin && sessionUserLoginType === 'USER' && (
                        <Grid item xs={12}>
                          <Typography variant="h6" sx={mobileSectionTitleSx}>{"회사"}</Typography>
                        </Grid>
                      )}
                      {isMobile && !isAdmin ? (
                        <Grid item xs={12} sx={{ display: mobileTab === 1 ? 'block' : 'none' }}>
                          <ReadOnlyField label="사업자등록번호" value={profileValues.bizRegNumber} />
                          <ReadOnlyField label="회사명" value={profileValues.name} />
                          <ReadOnlyField label="대표자" value={profileValues.ceo} />
                          <ReadOnlyField label="대표전화번호" value={profileValues.phone} />
                          <ReadOnlyField label="대표팩스번호" value={profileValues.fax} />
                          <ReadOnlyField label="주소" value={profileValues.address} />
                          <ReadOnlyField label="회사대표메일" value={profileValues.email} />
                        </Grid>
                      ) : (
                      <>
                      <Grid item xs={12}>
                        <FormInputText
                          autoComplete="bizRegNumber"
                          name={"bizRegNumber"}
                          control={control}
                          required
                          fullWidth
                          label={"사업자등록번호"}
                          inputProps={{ readOnly: true }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormInputText
                          autoComplete="name"
                          name={"name"}
                          control={control}
                          required
                          fullWidth
                          label={"회사명"}
                          inputProps={{ readOnly: sessionUserRole !== 'ADMIN_ROLE' && true }}
                          errorStyle={{ message: false, border: true }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormInputText
                          autoComplete="ceo"
                          name={"ceo"}
                          control={control}
                          required
                          fullWidth
                          label={"대표자"}
                          inputProps={{ readOnly: sessionUserRole !== 'ADMIN_ROLE' && true }}
                          errorStyle={{ message: false, border: true }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormInputText
                          autoComplete="phone"
                          name={"phone"}
                          control={control}
                          required
                          fullWidth
                          label={"대표전화번호"}
                          placeholder={isMobile ? undefined : "대표전화번호를 입력해주세요. - 는 자동입력됩니다."}
                          InputProps={{
                            inputComponent: TextMaskCustom,  // TODO : 이렇게 바깥에서 설정할지 FormInputText 안으로 넣을지 고려할 것
                            inputProps: {
                              style: { // TODO : 넘기는 방법을 정확히 몰라 style에 실어 넘김
                                // mask: "*00-*000-0000",
                                mask: phoneMask,
                                // definitions: {
                                //   '#': /[1-9]/,
                                // },
                              },
                              readOnly: sessionUserRole !== 'ADMIN_ROLE' && true
                            }
                          }}
                          errorStyle={{ message: false, border: true }}
                          onChange={(e) => handleChangePhone(e, 'phone')}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormInputText
                          autoComplete="fax"
                          name={"fax"}
                          control={control}
                          fullWidth
                          label={"대표팩스번호"}
                          placeholder={isMobile ? undefined : "대표팩스번호를 입력해주세요. - 는 자동입력됩니다."}
                          InputProps={{
                            inputComponent: TextMaskCustom, 
                            inputProps: {
                              style: { 
                                mask: phoneMask,
                              },
                              readOnly: sessionUserRole !== 'ADMIN_ROLE' && true
                            }
                          }}
                          errorStyle={{ message: false, border: true }}
                          onChange={(e) => handleChangePhone(e, 'fax')}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormInputText
                          autoComplete="address"
                          name={"address"}
                          control={control}
                          required
                          fullWidth
                          label={"주소"}
                          multiline={isMobile}
                          minRows={isMobile ? 2 : undefined}
                          inputProps={{ readOnly: sessionUserRole !== 'ADMIN_ROLE' && true }}
                          errorStyle={{ message: false, border: true }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={sessionUserRole === 'ADMIN_ROLE' ? 8 : 10}>
                        <FormInputText
                          autoComplete="email"
                          name={"email"}
                          control={control}
                          required
                          fullWidth
                          label={"회사대표메일"}
                          inputProps={{ readOnly: sessionUserRole !== 'ADMIN_ROLE' && true }}
                          errorStyle={{ message: false, border: true }}
                        />
                      </Grid>
                      </>
                      )}
                      {isMobile && isAdmin ? (
                        <Grid item xs={12}>
                          <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                            {sessionUserRole === 'ADMIN_ROLE' && (
                              <Button
                                fullWidth
                                variant="text"
                                startIcon={<Email />}
                                endIcon={<ChevronRight />}
                                onClick={() => setOpenEmailAppPasswordDialog(true)}
                                sx={mobileActionRowSx}
                              >
                                {"메일서명 설정"}
                              </Button>
                            )}
                            <Button
                              fullWidth
                              variant="text"
                              startIcon={<ContactMail />}
                              endIcon={<ChevronRight />}
                              onClick={() => setOpenAddressBookDialog(true)}
                              sx={mobileActionRowSx}
                            >
                              {"주소록"}
                            </Button>
                            {sessionUserLoginType === 'GCLIENT' && (
                              <Button
                                fullWidth
                                variant="text"
                                startIcon={<Password />}
                                endIcon={<ChevronRight />}
                                onClick={handleClickPasswordChange}
                                sx={mobileActionRowSx}
                              >
                                {"비밀번호 변경"}
                              </Button>
                            )}
                          </Box>
                        </Grid>
                      ) : !isMobile ? (
                        <>
                      {
                        sessionUserRole === 'ADMIN_ROLE' && (
                          <Grid item xs={12} sm={2} display="flex" justifyContent={"flex-end"}>
                            <Button fullWidth onClick={() => setOpenEmailAppPasswordDialog(true)} startIcon={<Email />} variant="outlined">
                              {"메일서명 설정"}
                            </Button>
                          </Grid>
                        )
                      }
                      <Grid item xs={12} sm={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button fullWidth onClick={() => setOpenAddressBookDialog(true)} startIcon={<ContactMail />} variant="outlined">
                           {"주소록"}
                        </Button>
                      </Grid>
                        </>
                      ) : null}
                      <Grid item xs={12} sx={{ display: isMobile && !isAdmin && mobileTab !== 1 ? 'none' : 'block' }}>
                        {isMobile ? (
                          <MobileClientTypes
                            isAdmin={isAdmin}
                            gclientTypes={gclientTypes}
                            selectedGClientTypeIds={selectedGClientTypeIds}
                            groups={gclientTypeAndGClientType04docu.filter((group) => group.gclientType04docus.length > 0)}
                            activeTypeId={expandedMobileTypeId}
                            onToggleType={toggleMobileGClientType}
                            onToggleDetail={handleChangeGClientTypeAndGClientType04docu}
                            onExpandType={setExpandedMobileTypeId}
                          />
                        ) : (
                        <Card sx={{ p: 2 }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Box sx={{ p: 1, border: '1px solid #E0E0E0', background: '#F2F2F2', borderRadius: 1 }}>
                                {"대분류"}
                              </Box>
                            </Grid>
                            <Grid item xs={12}>
                              <Box
                                sx={{
                                  overflowX: { xs: 'auto', md: 'visible' },
                                  overflowY: 'hidden',
                                  width: '100%',
                                  '&::-webkit-scrollbar': { height: 6 },
                                  '&::-webkit-scrollbar-thumb': { borderRadius: 3, bgcolor: 'grey.400' },
                                }}
                              >
                                <ToggleButtonGroup
                                  value={selectedGClientTypeIds}
                                  onChange={handleChangeGClientTypes}
                                  aria-label="GClient Types"
                                  color="primary"
                                  size="small"
                                  sx={{ 
                                    display: 'flex',
                                    flexWrap: { xs: 'nowrap', md: 'wrap' },
                                    rowGap: 1,
                                    flexShrink: 0,
                                    '& .MuiToggleButton-root': {
                                      py: { xs: 0.5, md: 1 },
                                      px: { xs: 1, md: 1.5 },
                                      fontSize: { xs: '0.75rem', md: '0.875rem' },
                                    },
                                  }}
                                >
                                  {
                                    gclientTypes.map((type, index) => {
                                      const { id, name } = type;
                                      return (
                                        <ToggleButton
                                          key={id || `gclientType-${index}`}
                                          value={id}
                                          aria-label={`대분류-${name}`}
                                          sx={{
                                            fontWeight: 'bold',
                                            flexShrink: 0,
                                          }}
                                          disabled={sessionUserRole === 'ADMIN_ROLE' ? false : true}
                                        >
                                          {name}
                                        </ToggleButton>
                                      )
                                    })
                                  }
                                </ToggleButtonGroup>
                              </Box>
                            </Grid>
                            {
                              gclientTypeAndGClientType04docu && gclientTypeAndGClientType04docu.filter(gclientType => gclientType.show && gclientType.gclientType04docus.length > 0).length > 0 ? (
                                <>
                                  <Grid item xs={12}>
                                    <Box sx={{ p: 1, border: '1px solid #E0E0E0', background: '#F2F2F2', borderRadius: 1 }}>
                                      {"세분류"}
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Card sx={{ p: 2 }}>
                                      <Grid container spacing={1}>
                                      {
                                        gclientTypeAndGClientType04docu.map((gclientType, idx) => {
                                          const { gclientTypeId, gclientTypeName, gclientType04docus, show, selectedGClientType04docuIds } = gclientType;
                                          return (
                                            <>
                                              {
                                                show && gclientType04docus.length > 0 ? (
                                                  <Grid item xs={12}>
                                                    <Box sx={{ p: 1, border: '1px solid #E0E0E0', background: '#F2F2F2', borderRadius: 1 }}>
                                                      {gclientTypeName}
                                                    </Box>
                                                  </Grid>
                                                ) : (<></>)
                                              }
                                              {
                                                show && gclientType04docus.length > 0 ? (
                                                  <Grid item xs={12}>
                                                    <Box
                                                      sx={{
                                                        overflowX: { xs: 'auto', md: 'visible' },
                                                        overflowY: 'hidden',
                                                        width: '100%',
                                                        '&::-webkit-scrollbar': { height: 6 },
                                                        '&::-webkit-scrollbar-thumb': { borderRadius: 3, bgcolor: 'grey.400' },
                                                      }}
                                                    >
                                                      <ToggleButtonGroup
                                                        value={selectedGClientType04docuIds}
                                                        onChange={(e, selectedIds) => handleChangeGClientTypeAndGClientType04docu(e, selectedIds, gclientTypeId, idx)}
                                                        aria-label="GClient Types"
                                                        color="primary"
                                                        size="small"
                                                        sx={{ 
                                                          display: 'flex',
                                                          flexWrap: { xs: 'nowrap', md: 'wrap' },
                                                          rowGap: 1,
                                                          flexShrink: 0,
                                                          '& .MuiToggleButton-root': {
                                                            py: { xs: 0.5, md: 1 },
                                                            px: { xs: 1, md: 1.5 },
                                                            fontSize: { xs: '0.75rem', md: '0.875rem' },
                                                          },
                                                        }}
                                                      >
                                                        {
                                                          gclientType.gclientType04docus.map(type => {
                                                            const { id, name } = type;
                                                            return (
                                                              <ToggleButton
                                                                key={id}
                                                                value={id}
                                                                aria-label={`세분류-${name}`}
                                                                sx={{
                                                                  fontWeight: 'bold',
                                                                  flexShrink: 0,
                                                                }}
                                                                disabled={sessionUserRole === 'ADMIN_ROLE' ? false : true}
                                                              >
                                                                {name}
                                                              </ToggleButton>
                                                            )
                                                          })
                                                        }
                                                      </ToggleButtonGroup>
                                                    </Box>
                                                  </Grid>
                                                ) : (<></>)
                                              }
                                            </>
                                          )
                                        })
                                      }
                                      </Grid>
                                    </Card>
                                  </Grid>
                                </>
                              ) : (<></>)
                            }
                          </Grid>
                        </Card>
                        )}
                      </Grid>
                      <Grid item xs={12} sx={{ order: isMobile && isAdmin && sessionUserLoginType === 'USER' ? -1 : 0, display: isMobile && !isAdmin && mobileTab !== 0 ? 'none' : 'block' }}>
                        <Grid container spacing={2}>
                        {
                          (sessionUserRole === 'USER_ROLE' || (sessionUserLoginType === 'USER' && sessionUserRole === 'ADMIN_ROLE')) && (
                            <>
                              {!(isMobile && !isAdmin) && (
                              <Grid item xs={12}>
                                <Box sx={{ p: 1, border: '1px solid #E0E0E0', background: '#F2F2F2', borderRadius: 1 }}>
                                  <Grid container>
                                    <Grid item xs={12} sm={10} display="flex" alignItems="center">
                                      {"내정보"}
                                    </Grid>
                                      <Grid item xs={12} sm={2} display="flex" justifyContent="flex-end" alignItems="center">
                                        <Button
                                          onClick={handleClickPasswordChange}
                                          fullWidth
                                          variant="outlined"
                                          startIcon={<Password />}
                                        >
                                          {"비밀번호 변경"}
                                        </Button>
                                      </Grid>
                                  </Grid>
                                </Box>
                              </Grid>
                              )}
                              {isMobile && !isAdmin && sessionUserLoginType === 'USER' ? (
                                <Grid item xs={12}>
                                  <ReadOnlyField label="사용자 아이디(이메일)" value={profileValues.userId} />
                                </Grid>
                              ) : (
                              <Grid item xs={12} sm={sessionUserLoginType === 'USER' ? 10 : 12}>
                                <FormInputText
                                  autoComplete="userId"
                                  required
                                  fullWidth
                                  label="사용자 아이디(이메일)"
                                  name={"userId"}
                                  control={control}
                                  errorStyle={{ message: false, border: true }}
                                  inputProps={{ readOnly: true }}
                                />
                              </Grid>
                              )}
                              {
                                sessionUserLoginType === 'USER' && !(isMobile && !isAdmin) && (
                                  <Grid item xs={12} sm={2} display="flex" justifyContent={"flex-end"}>
                                    <Button fullWidth onClick={() => setOpenEmailAppPasswordDialogUser(true)} startIcon={<Email />} variant="outlined">
                                      {"메일서명 설정"}
                                    </Button>
                                  </Grid>
                                )
                              }
                              <Grid item xs={12}>
                                <FormInputText
                                  autoComplete="userName"
                                  name={"userName"}
                                  control={control}
                                  required
                                  fullWidth
                                  label={"이름"}
                                  errorStyle={{ message: false, border: true }}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <FormInputText
                                  autoComplete="mobile"
                                  name={"mobile"}
                                  control={control}
                                  required
                                  fullWidth
                                  label={"모바일"}
                                  InputProps={{
                                    inputComponent: TextMaskCustom,  // TODO : 이렇게 바깥에서 설정할지 FormInputText 안으로 넣을지 고려할 것
                                    inputProps: {
                                      style: { // TODO : 넘기는 방법을 정확히 몰라 style에 실어 넘김
                                        // mask: "000-0000-0000",
                                        mask: mobileMask,
                                        definitions: {
                                          '#': /[1-9]/,
                                        },
                                      },
                                    }
                                  }}
                                  errorStyle={{ message: false, border: true }}
                                  onChange={handleChangeMobile}
                                />
                              </Grid>
                              {isMobile && !isAdmin && sessionUserLoginType === 'USER' && (
                                <Grid item xs={12}>
                                  <LoadingButton
                                    onClick={handleSubmit(onSubmit)}
                                    fullWidth
                                    variant="contained"
                                    loading={loading}
                                  >
                                    {"수정하기"}
                                  </LoadingButton>
                                </Grid>
                              )}
                              {isMobile && !isAdmin && (
                                <Grid item xs={12}>
                                  <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                                    <Button
                                      fullWidth
                                      variant="text"
                                      startIcon={<Email />}
                                      endIcon={<ChevronRight />}
                                      onClick={() => setOpenEmailAppPasswordDialogUser(true)}
                                      sx={mobileActionRowSx}
                                    >
                                      {"메일서명 설정"}
                                    </Button>
                                    <Button
                                      fullWidth
                                      variant="text"
                                      startIcon={<ContactMail />}
                                      endIcon={<ChevronRight />}
                                      onClick={() => setOpenAddressBookDialog(true)}
                                      sx={mobileActionRowSx}
                                    >
                                      {"주소록"}
                                    </Button>
                                    <Button
                                      fullWidth
                                      variant="text"
                                      startIcon={<Password />}
                                      endIcon={<ChevronRight />}
                                      onClick={handleClickPasswordChange}
                                      sx={mobileActionRowSx}
                                    >
                                      {"비밀번호 변경"}
                                    </Button>
                                  </Box>
                                </Grid>
                              )}
                              </>
                          )
                        }
                        </Grid>
                      </Grid>
                      {!(isMobile && isAdmin) && !(isMobile && !isAdmin && sessionUserLoginType === 'USER') && (
                      <Grid item xs={12}>
                        <LoadingButton
                          onClick={handleSubmit(onSubmit)}
                          fullWidth
                          variant="contained"
                          loading={loading}
                        >
                          {"수정하기"}
                        </LoadingButton>
                      </Grid>
                      )}
                    </Grid>
              </ProfileSection>
               {/* 담당자 */}
              { isAdmin && (
                <Grid item xs={12} sx={{ display: isMobile && mobileTab !== 1 ? 'none' : 'block' }}>
                  <Users embedded={isMobile} />
                </Grid>
              ) }
              {/* 날인정보 */}
              <ProfileSection
                accordion={!isMobile}
                expanded={signExpanded}
                onChange={() => setSignExpanded(!signExpanded)}
                hidden={isMobile && mobileTab !== 2}
                summary={(
                  <AccordionSummary aria-controls="sign-content" id="sign-header">
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="h6" component="div">{"날인정보"}</Typography>
                      </Grid>
                    </Grid>
                  </AccordionSummary>
                )}
              >
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <CertifiedBox
                              gclient={selectedGClient}
                              control={control}
                              setValue={setValue}
                              getValues={getValues}
                              defaultValues={defaultValues}
                            />
                          </Grid>
                          {
                            isAdmin && !isMobile && (
                              <Grid item xs={12}>
                                <LoadingButton
                                  onClick={onSubmitStamp}
                                  fullWidth
                                  variant="contained"
                                  loading={loading}
                                >
                                  {"문구 저장"}
                                </LoadingButton>
                              </Grid>
                            )
                          }
                        </Grid>
                      </Grid>
                    </Grid>
              </ProfileSection>
              {/* 보안 - 정보공개여부 */}
              { isAdmin && (
                <Grid item xs={12} sx={{ display: isMobile && mobileTab !== 3 ? 'none' : 'block' }}>
                  <Security embedded={isMobile} />
                </Grid>
              ) }
              {/* 일반서류부터 하단 - 모바일에서는 렌더링하지 않음 (API 호출 방지) */}
              {!isMobile && (
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    {/* 일반서류 - 모든 회원사에 반드시 있어야 하므로 Grid가 G04GeneralDocu 바깥에 있음 */}
                    <Grid item xs={12}>
                      <G04GeneralDocu
                        handleClickFileDownload={handleClickFileDownload}
                        handleClickViewEachDoc={handleClickViewEachDoc}
                        selectedGClient={selectedGClient}
                      />
                    </Grid>
                    {/* 인증서 - 일반서류를 제외한 인증서~기타는 회원사에 따라 해당 문서가 없을 수도 있으므로 Grid가 컴포넌트 안쪽에 있음. 바깥에 있으면 Grid가 공간 차지 */}
                    <G04docuGCertification
                      handleClickFileDownload={handleClickFileDownload}
                      handleClickViewEachDoc={handleClickViewEachDoc}
                      selectedGClient={selectedGClient}
                    />
                    {/* 원자재 */}
                    <G04docuGTestRawMaterial
                      handleClickFileDownload={handleClickFileDownload}
                      handleClickViewEachDoc={handleClickViewEachDoc}
                      selectedGClient={selectedGClient}
                    />
                    {/* 가공부자재 */}
                    <G04docuGTestSubMaterial
                      division={'SUB_MATERIAL_PROCESS'}
                      handleClickFileDownload={handleClickFileDownload}
                      handleClickViewEachDoc={handleClickViewEachDoc}
                      selectedGClient={selectedGClient}
                    />
                    {/* 시공부자재 */}
                    <G04docuGTestSubMaterial
                      division={'SUB_MATERIAL_BUILD'}
                      handleClickFileDownload={handleClickFileDownload}
                      handleClickViewEachDoc={handleClickViewEachDoc}
                      selectedGClient={selectedGClient}
                    />
                    {/* 가공 */}
                    <G04docuGTestProcess
                      handleClickFileDownload={handleClickFileDownload}
                      handleClickViewEachDoc={handleClickViewEachDoc}
                      selectedGClient={selectedGClient}
                    />
                    {/* 기타 */}
                    <G04docuEtc
                      handleClickFileDownload={handleClickFileDownload}
                      handleClickViewEachDoc={handleClickViewEachDoc}
                      selectedGClient={selectedGClient}
                    />
                    <Grid item xs={12}>
                      <LoadingButton
                        onClick={handleClickLeave}
                        fullWidth
                        variant="outlined"
                        loading={loading}
                      >
                        {"탈퇴하기"}
                      </LoadingButton>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Box>
        </Box>
        {/* 위로 가기 버튼 */}
        <Zoom in={trigger}>
            <Fab
              onClick={scrollToTop}
              color="primary"
              size="small"
              aria-label="scroll back to top"
              sx={{
                position: 'fixed',
                bottom: showMobileSaveBar ? 80 : 16,
                right: isMobile ? 16 : 300,
              }}
            >
              <KeyboardArrowUpIcon />
            </Fab>
          </Zoom>
      </Container>
      {showMobileSaveBar && (
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            p: 1.5,
            bgcolor: '#fff',
            borderTop: '1px solid #dcdcdc',
            zIndex: (t) => t.zIndex.appBar,
          }}
        >
          <LoadingButton
            onClick={mobileTab === 2 ? onSubmitStamp : handleSubmit(onSubmit)}
            fullWidth
            variant="contained"
            loading={loading}
          >
            {mobileTab === 2 ? "문구 저장" : "수정하기"}
          </LoadingButton>
        </Box>
      )}
      <PasswordChangeDialog
        open={openPasswordDialog}
        setOpen={setOpenPasswordDialog}
        mode={mode}
        setMode={setMode}
        // setCheckValue={setValue}
        // type={type}
        // setType={setType}
        // credential={getValues("bizRegNumber")}
      />
      <EmailSettingDialog
        type={"GCLIENT"}
        open={openEmailAppPasswordDialog}
        setOpen={setOpenEmailAppPasswordDialog}
        credential={getValues("bizRegNumber")}
        email={getValues("email")}
      />
      <EmailSettingDialog
        type={"USER"}
        open={openEmailAppPasswordDialogUser}
        setOpen={setOpenEmailAppPasswordDialogUser}
        credential={getValues("userId")}
        email={getValues("userId")}
      />
      <AlertDialog
        alertInfo={alertInfo}
        setAlertInfo={setAlertInfo}
      />
      <ConfirmDialog
        removeId={leaveObject}
        title={"탈퇴안내"}
        open={confirmOpenLeave}
        setOpen={setConfirmOpenLeave}
        onConfirm={leave}
        onCancel={() => {}}
      >
        {
          sessionUserLoginType === 'GCLIENT' && (<>
            <span style={{ color: '#1976d2' }}>{"탈퇴하시면 3개월 이내 재가입이 되지 않습니다."}</span><br /><br />
            <span>{"그래도 탈퇴하시겠습니까?"}</span>
          </>)
        }
        {
          sessionUserLoginType === 'USER' && (<>
            <span style={{ color: '#1976d2' }}>{"탈퇴하시면 사용하셨던 자료는 열람이 되지 않습니다."}</span><br />
            <span style={{ color: "#ff5722" }}>{"추후 동일한 이메일 아이디로 가입하셔도 열람이 되지 않습니다."}</span><br /><br />
            <span>{"그래도 탈퇴하시겠습니까?"}</span>
          </>)
        }
      </ConfirmDialog>
      <AddressBookDialog
        type={"USER"}
        open={openAddressBookDialog}
        setOpen={setOpenAddressBookDialog}
        credential={getValues("userId")}
        email={getValues("userId")}
      />
      <DocumentViewerDrawer 
        open={documentViewerDrawerOpen}
        onClose={() => setDocumentViewerDrawerOpen(false)}
        url={currentUrl}
      />
    </ThemeProvider>
  );
}

export default Profile;